export interface DefinitionComponent {
  type: string;
  code: string;
  name: string;
  fieldPath?: string;
  readOnly?: boolean;
}

export type SectionType = 'simple' | 'multiple' | 'jurisdiction';

export enum FormCellPurpose {
  TaxReturnTotal = 'TaxReturnTotal',
  JurisdictionTotal = 'JurisdictionTotal'
}

export interface SectionComponent extends DefinitionComponent {
  cells: Array<CellComponent>;
  minInstances?: number | null;
  maxInstances?: number | null;
  effectiveMinInstances?: number;
  effectiveMaxInstances?: number | null;
  sectionType?: SectionType;
  jurisdictions?: Array<Jurisdiction>;
}

export interface Jurisdiction {
  id: string;
  name: string;
  color: string;
}

export interface CellComponent extends DefinitionComponent {
  description?: string;
  catalogName: string;
  defaultValue?: any;
  isFormula: boolean;
  mandatory: boolean;
  editable: boolean;
  visible: boolean;
  cellPurpose: string;
}

export interface RowDefinition {
  id: string;
  cols: Array<CellComponent>;
}

export interface FormDefinitionCellLike {
  code: string;
  type: string;
  cellPurpose?: FormCellPurpose | null;
}

export interface FormDefinitionSectionLike {
  code: string;
  cells: Array<FormDefinitionCellLike>;
  minInstances?: number | null;
  maxInstances?: number | null;
  effectiveMinInstances?: number;
  effectiveMaxInstances?: number | null;
  sectionType?: SectionType;
}

export interface FormDefinitionLike {
  name: string;
  sections: Array<FormDefinitionSectionLike>;
}

export interface Period {
  year: number;
  taxPeriodicity: string;
  taxPeriodicityTypeId: string;
  taxPeriodicityItemId: string;
}

export interface TaxReturnCell {
  code: number;
  type?: string;
  value: any;
  cellPurpose?: FormCellPurpose | null;
  sectionRowId?: string | null;
}

export interface CellChanged<TCell extends TaxReturnCell = TaxReturnCell> {
  cell: TCell;
}

export interface FormulaFailed<TCell extends TaxReturnCell = TaxReturnCell> {
  taxReturnCell: TCell;
  exceptionMessage: string;
  exceptionStackTrace?: string;
}

export type TaxReturnRequest<
  TCell extends TaxReturnCell = TaxReturnCell,
  TPeriod extends object = Partial<Period>
> = TPeriod & {
  formVersionId: string;
  cells: Array<TCell>;
  taxpayerId: string;
  amendedTaxReturnId?: string;
  amendedTaxReturnNumber?: string;
};

export type TaxReturnCellLike = TaxReturnCell;

export type TaxReturnCellChangedEventLike<TCell extends TaxReturnCell = TaxReturnCell> =
  CellChanged<TCell>;

export type TaxReturnFormulaFailedLike<TCell extends TaxReturnCell = TaxReturnCell> =
  FormulaFailed<TCell>;

export interface TaxReturnRequestRuntime<
  TDefinition extends FormDefinitionLike = FormDefinitionLike,
  TPeriod = object
> {
  formVersionId?: string;
  def?: TDefinition;
  taxpayerId?: string;
  readOnly?: boolean;
  period?: TPeriod;
  amendedTaxReturnId?: string;
}

export interface TaxReturnCalculationConnection<
  TCellChanged extends TaxReturnCellChangedEventLike = TaxReturnCellChangedEventLike,
  TFormulaFailed extends TaxReturnFormulaFailedLike = TaxReturnFormulaFailedLike
> {
  state?: string;
  send: (methodName: string, payload: unknown) => Promise<unknown>;
  on: {
    (eventName: 'CellChanged', handler: (payload: TCellChanged) => void): void;
    (eventName: 'FormulaFailed', handler: (payload: TFormulaFailed) => void): void;
  };
  off: {
    (eventName: 'CellChanged', handler: (payload: TCellChanged) => void): void;
    (eventName: 'FormulaFailed', handler: (payload: TFormulaFailed) => void): void;
  };
}

export type HookFormValidationMode = 'onBlur' | 'onChange' | 'onSubmit' | 'onTouched' | 'all';

export const strategiesType = {
  numeric: 'numeric',
  currency: 'currency',
  text: 'text',
  list: 'list',
  date: 'date'
};
